import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ProductFormValues } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { productCapabilities } from '@/auth/product-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { type ProductDTO, productService } from '@/services/product.service'
import { type BaseFormValues, baseFormErrors, EMPTY_BASE_FORM, toCreateBody, toPatchBody } from './product-form'
import { productFieldError, translateKeys } from './product-ui'
import { useTenantLocales } from './useTenantLocales'

/** The form values an existing product starts from. */
export const valuesFromProduct = (p: ProductDTO): BaseFormValues => ({
	code: p.code,
	name: p.name,
	defaultLocale: p.defaultLocale,
	categoryId: p.categoryId ?? '',
	featured: p.featured,
	icon: p.icon ?? '',
	partnerAttributes: Object.keys(p.partnerAttributes).length > 0 ? JSON.stringify(p.partnerAttributes, null, 2) : '',
	ctaPrimaryType: p.ctaPrimaryType ?? '',
	ctaPrimaryTarget: p.ctaPrimaryTarget ?? '',
	ctaSecondaryType: p.ctaSecondaryType ?? '',
	ctaSecondaryTarget: p.ctaSecondaryTarget ?? '',
})

/** The product query shared by every editor section. */
export const productKey = (id: string) => ['product', id]

/** POST (create, then open the product) or a code-less PATCH (edit); the result is written to the cache. */
function useSaveProduct(
	product: ProductDTO | undefined,
	values: BaseFormValues,
	handlers: { onSaved: () => void; onError: (error: unknown) => void },
) {
	const navigate = useNavigate()
	const client = useQueryClient()
	return useMutation({
		mutationFn: () =>
			product
				? productService.update(product.productId, toPatchBody(values))
				: productService.create(toCreateBody(values)),
		onSuccess: (result) => {
			client.setQueryData(productKey(result.productId), result)
			handlers.onSaved()
			if (!product) void navigate(`/products/${result.productId}`)
		},
		onError: handlers.onError,
	})
}

/**
 * Base + CTA form of the product editor (TO-416 product-admin-ui REQ-003,
 * REQ-004): client rules first, then POST (create) or a code-less PATCH (edit),
 * with server codes mapped onto their field.
 */
export function useProductEditor(product: ProductDTO | undefined) {
	const { t, i18n } = useTranslation('products')
	const caps = productCapabilities(useViewerPermissions().permissions)
	const { settings } = useTenantLocales()
	const categories = useQuery({ queryKey: ['product-categories'], queryFn: productService.allCategories })
	const mode = product ? 'edit' : 'create'
	const [draft, setDraft] = useState<BaseFormValues | null>(null)
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [formError, setFormError] = useState<string | null>(null)
	const [saved, setSaved] = useState(false)
	const values: BaseFormValues =
		draft ??
		(product ? valuesFromProduct(product) : { ...EMPTY_BASE_FORM, defaultLocale: settings.defaultLocale ?? '' })
	const save = useSaveProduct(product, values, {
		onSaved: () => {
			setDraft(null)
			setSaved(Boolean(product))
		},
		onError: (error) => {
			const { field, message } = productFieldError(error, t, (key) => i18n.exists(key, { ns: 'products' }), 'base')
			if (field === 'form') setFormError(message)
			else setErrors({ [field]: message })
		},
	})

	return {
		t,
		mode: mode as 'create' | 'edit',
		values: values as ProductFormValues,
		errors,
		formError,
		saved,
		locales: settings.enabledLocales,
		categories: (categories.data ?? []).map((c) => ({ id: c.categoryId, label: c.name })),
		readOnly: !caps.write,
		isBusy: save.isPending,
		onChange: (field: keyof ProductFormValues, value: string | boolean) => {
			setDraft({ ...values, [field]: value })
			setSaved(false)
		},
		onSubmit: () => {
			const found = baseFormErrors(values, mode)
			setErrors(translateKeys(found, t))
			setFormError(null)
			if (Object.keys(found).length === 0) save.mutate()
		},
	}
}
